import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, X, ShoppingBag, ArrowRight, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

export const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, cartTotal } = useCart();

  const shippingCost = cartTotal >= 999 ? 0 : 99;
  const totalWithShipping = cartTotal + shippingCost;

  if (cartItems.length === 0) {
    return (
      <main className="pt-32 pb-24 bg-[#FAF8F5]" data-testid="cart-page-empty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 mx-auto bg-[#E8E4DE] rounded-full flex items-center justify-center mb-6">
              <ShoppingBag className="text-[#8B7355]" size={32} />
            </div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-[#2D4A3E] mb-4">
              Your Cart is Empty
            </h1>
            <p className="font-['Montserrat'] text-[#5A6B5D] mb-8">
              Looks like you haven't added any items yet
            </p>
            <Link to="/category/all">
              <Button 
                className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] px-8 py-6 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat']"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24 bg-[#FAF8F5]" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mb-8 md:mb-12" data-testid="cart-title">
            Shopping Cart
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="border-b border-[#E8E4DE] pb-4 mb-6 hidden md:grid grid-cols-12 gap-4">
              <span className="col-span-6 font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355]">Product</span>
              <span className="col-span-2 font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355] text-center">Quantity</span>
              <span className="col-span-2 font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355] text-right">Price</span>
              <span className="col-span-2 font-['Montserrat'] text-xs uppercase tracking-wider text-[#8B7355] text-right">Total</span>
            </div>

            <div className="space-y-4">
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white border border-[#E8E4DE] p-4 md:p-0 md:border-0 md:bg-transparent md:grid md:grid-cols-12 gap-4 items-center md:py-6 md:border-b"
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Product */}
                  <div className="md:col-span-6 flex gap-4">
                    <Link to={`/product/${item.id}`} className="w-20 h-20 sm:w-24 sm:h-24 bg-[#FAF8F5] flex-shrink-0 border border-[#E8E4DE]">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${item.id}`}>
                        <h3 className="font-['Cormorant_Garamond'] text-lg text-[#2D4A3E] hover:text-[#C67D4E] transition-colors">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="font-['Montserrat'] text-xs text-[#8B7355] capitalize mt-1">
                        {item.category.replace('-', ' ')}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="flex items-center gap-1 text-xs text-[#8B7355] hover:text-red-500 mt-2 font-['Montserrat'] md:hidden"
                        data-testid={`remove-item-mobile-${item.id}`}
                      >
                        <X size={12} /> Remove
                      </button>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="md:col-span-2 flex justify-start md:justify-center mt-4 md:mt-0">
                    <div className="flex items-center border border-[#E8E4DE] bg-white">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-[#FAF8F5] transition-colors"
                        data-testid={`decrease-qty-${item.id}`}
                      >
                        <Minus size={14} className="text-[#2D4A3E]" />
                      </button>
                      <span className="px-4 font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]" data-testid={`qty-${item.id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-[#FAF8F5] transition-colors"
                        data-testid={`increase-qty-${item.id}`}
                      >
                        <Plus size={14} className="text-[#2D4A3E]" />
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="md:col-span-2 text-right hidden md:block">
                    <span className="font-['Montserrat'] text-[#2D4A3E]">${item.price.toLocaleString()}</span>
                  </div>

                  {/* Total */}
                  <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-4 mt-4 md:mt-0">
                    <span className="font-['Montserrat'] font-semibold text-[#2D4A3E]">
                      ${(item.price * item.quantity).toLocaleString()}
                    </span>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="hidden md:block text-[#8B7355] hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8">
              <Link 
                to="/category/all"
                className="inline-flex items-center gap-2 font-['Montserrat'] text-xs uppercase tracking-wider text-[#5A6B5D] hover:text-[#2D4A3E] transition-colors"
                data-testid="continue-shopping-link"
              >
                <ArrowRight size={14} className="rotate-180" />
                Continue Shopping
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E8E4DE] p-6 md:p-8 sticky top-32" data-testid="order-summary">
              <h2 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-6">Order Summary</h2>

              <div className="space-y-4 font-['Montserrat']">
                <div className="flex justify-between text-[#5A6B5D]">
                  <span>Subtotal</span>
                  <span>${cartTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-[#5A6B5D]">
                  <span>Shipping</span>
                  <span className={shippingCost === 0 ? 'text-[#10B981] font-semibold' : ''}>
                    {shippingCost === 0 ? 'FREE' : `$${shippingCost}`}
                  </span>
                </div>
                {shippingCost > 0 && (
                  <p className="text-xs text-[#C67D4E]">
                    Add ${(999 - cartTotal).toLocaleString()} more for free shipping
                  </p>
                )}
                <div className="border-t border-[#E8E4DE] pt-4">
                  <div className="flex justify-between text-lg font-semibold text-[#2D4A3E]">
                    <span>Total</span>
                    <span data-testid="cart-total">${totalWithShipping.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <Link to="/checkout" className="block mt-8">
                <Button 
                  className="w-full bg-[#C67D4E] text-white hover:bg-[#A86840] py-6 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat']"
                  data-testid="proceed-checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </Link>

              <div className="mt-6 pt-6 border-t border-[#E8E4DE]">
                <div className="flex items-center gap-3 text-sm text-[#5A6B5D]">
                  <Truck size={18} className="text-[#C67D4E]" />
                  <span className="font-['Montserrat'] text-xs">Free shipping on orders over $999</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};
